// src/contexts/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { authAPI, userPreferencesAPI } from '../services/api';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:5001';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  msgBackupAlerts: boolean;
  msgQuoteAlerts: boolean;
  msgBookingReminders: boolean;
  msgSystemToasts: boolean;
  accessibilityMode: boolean;
  showPerformanceIndicator: boolean;
  showOnboarding: boolean;
  themeMode: 'light' | 'dark';
  locale: string;
}

// The subset of User that ProfilePage's preference toggles can patch.
// Kept separate from User so callers can't accidentally overwrite
// identity fields (id/username/email/role) through this path.
type UserPrefsPatch = Partial<
  Pick<
    User,
    | 'msgBackupAlerts'
    | 'msgQuoteAlerts'
    | 'msgBookingReminders'
    | 'msgSystemToasts'
    | 'accessibilityMode'
    | 'showPerformanceIndicator'
    | 'showOnboarding'
    | 'themeMode'
    | 'locale'
  >
>;

export interface LoginResult {
  success: boolean;
  message?: string;
  lockoutUntil?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  checkAuth: () => boolean;
  isAdmin: () => boolean;
  hasRole: (role: string) => boolean;
  // Merges a message-preference change into the in-memory user (and
  // localStorage-persisted authUser) without a re-login. Callers that
  // read user.msgSystemToasts etc. see the update immediately.
  updateUserPrefs: (patch: UserPrefsPatch) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const prefsEditedRef = useRef(false);

  // Preferences may have changed on another device since this blob was
  // cached. Skipped if the user has already edited one in this session, so a
  // slow response can't overwrite a newer local change.
  const refreshPrefsFromServer = () => {
    try {
      userPreferencesAPI
        .getMessagePreferences({ background: true })
        .then((serverPrefs) => {
          if (prefsEditedRef.current || !serverPrefs) return;
          setUser((prev) => {
            if (!prev) return prev;
            const updated = { ...prev, ...serverPrefs };
            localStorage.setItem('authUser', JSON.stringify(updated));
            return updated;
          });
        })
        .catch(() => {});
    } catch {
      // preferences refresh is best-effort
    }
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');

    if (storedToken && storedUser) {
      if (isTokenExpired(storedToken)) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      } else {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          refreshPrefsFromServer();
        } catch {
          localStorage.removeItem('authToken');
          localStorage.removeItem('authUser');
        }
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (
    username: string,
    password: string
  ): Promise<LoginResult> => {
    // Network/connectivity errors are re-thrown so callers can show contextual messages.
    // Credential/lockout failures (non-ok HTTP response) resolve with success: false.
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message,
        lockoutUntil: errorData.lockoutUntil,
      };
    }

    const data = await response.json();

    const userData: User = {
      id: data.userId,
      username: data.username,
      email: data.email,
      role: data.role,
      msgBackupAlerts: data.msgBackupAlerts ?? true,
      msgQuoteAlerts: data.msgQuoteAlerts ?? true,
      msgBookingReminders: data.msgBookingReminders ?? true,
      msgSystemToasts: data.msgSystemToasts ?? true,
      accessibilityMode: data.accessibilityMode ?? false,
      showPerformanceIndicator: data.showPerformanceIndicator ?? true,
      showOnboarding: data.showOnboarding ?? true,
      themeMode: data.themeMode ?? 'light',
      locale: data.locale ?? 'en-ZA',
    };

    localStorage.setItem('authToken', data.token);
    localStorage.setItem('authUser', JSON.stringify(userData));

    setToken(data.token);
    setUser(userData);

    return { success: true };
  };

  const logout = async (): Promise<void> => {
    // Best-effort server-side logout. If it fails (network error, expired
    // token, etc.) we still clear local state so the user is not trapped.
    try {
      await authAPI.logout();
    } catch {
      // swallow - local cleanup proceeds regardless
    }

    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setToken(null);
    setUser(null);
  };

  const checkAuth = (): boolean => !!token && !!user;

  const isAdmin = (): boolean => user?.role === 'Admin';

  const hasRole = (role: string): boolean => user?.role === role;

  const updateUserPrefs = (patch: UserPrefsPatch): void => {
    prefsEditedRef.current = true;
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...patch };
      localStorage.setItem('authUser', JSON.stringify(updated));
      return updated;
    });
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
    checkAuth,
    isAdmin,
    hasRole,
    updateUserPrefs,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
